// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import {
  useEvolvePaginatedGet,
  type ProductId,
  useEvolveGet,
  type AssignedUserRole,
  type RoleScope,
} from '@evolvemep/api-ts';
import { Anchor, Box, Card, Flex, Group, LoadingOverlay, Stack, Title, Text } from '@mantine/core';
import { Navigate } from 'react-router';

import { DesktopLayoutCard } from 'app/Layout/DesktopLayoutCard';
import { MobileUnsupportedGate } from 'app/Layout/MobileUnsupportedGate';
import { useCanAccessRoute } from 'app/Layout/NavBar/NavBarContent/useCanAccessRoute';
import { useAppShell } from 'app/Layout/NavBar/useAppShell';
import { ALL_MODULE_ROUTES } from 'app/ModuleRoutes';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { getLicenseIconSize, LicenseIcon } from 'assets/icons/LicenseIcon';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { UserInfo } from 'components/Mantine/UserInfo';
import { PRODUCT_IDS } from 'constants/globalConstants';
import { useGetAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { isKeyOf } from 'helpers/isKeyOf';
import { strongKeys } from 'helpers/strongEntries';
import { useUser } from 'hooks/user/useUser';
import { EvolveProductCard } from 'modules/Admin/Licenses/LicensesMantine/EvolveProductCard';

import { ResetPasswordModal } from './Layout/HeaderBar/ProfileDropdown/ResetPasswordModal/ResetPasswordModal';

const licensesThatNeedRoles: Record<ProductId, RoleScope> = {
  [PRODUCT_IDS.SHOP_LICENSE]: 'Shop',
  [PRODUCT_IDS.FABRICATION_LICENSE]: 'Fabrication',
};

export const NoAccessibleRoutesLandingPage = () => {
  const canAccessRoute = useCanAccessRoute();
  const { setNavbarExpanded } = useAppShell();
  useEffect(() => setNavbarExpanded(false), [setNavbarExpanded]);

  const { user, licenses } = useUser();
  const [passwordReset, setPasswordReset] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const { fetchPage } = useEvolvePaginatedGet('admin/user', {
    defaultConfig: {
      params: {
        isAdmin: true,
        companyId: user.companyId,
      },
    },
  });
  const [companyAdmins, loadingAdmins] = useGetAllDataFromFetcher(fetchPage, {});

  const { fetchPage: fetchUserRolesPage } = useEvolvePaginatedGet(`admin/role/assigned/${user.userId}`);
  const [userRoles, rolesLoading] = useGetAllDataFromFetcher(fetchUserRolesPage, {});

  const roleLookup: Record<ProductId, AssignedUserRole['displayName']> = useMemo(
    () =>
      userRoles.reduce(
        (prev, { productId, displayName }) => ({
          ...prev,
          [productId]: displayName,
        }),
        {},
      ),
    [userRoles],
  );

  const { data: licenseData, loading: loadingLicenses } = useEvolveGet('admin/company/licenses', {
    lazy: false,
  });

  const hasAccessibleRoute = useMemo(() => strongKeys(ALL_MODULE_ROUTES).some(canAccessRoute), [canAccessRoute]);

  if (passwordReset) return <Navigate to="/auth/login" />;

  if (hasAccessibleRoute) return <Navigate to="/" />;

  return (
    <MobileUnsupportedGate>
      <DesktopLayoutCard>
        <Box style={{ overflow: 'hidden auto' }}>
          <BasePageHeader title="Helpful Links" hideGlobalFilters />
          <Title order={4}>
            <UserInfo size="lg" user={user} fz="h4" />
          </Title>

          <Group align="stretch" my="md">
            {licenses.includes(PRODUCT_IDS.SYNC_DESKTOP_LICENSE) && (
              <Card flex={1} bg="gray.0" radius="sm" p="lg">
                <Group gap="sm" mb="lg">
                  <EvolveIcon icon="Info" size="md" color="primary" />
                  <Title order={4}>360 Sync Users</Title>
                </Group>
                <Text>
                  Your 360 Sync license is active. To get started, download and install the 360 Sync Desktop app on your
                  computer.
                </Text>
                <Anchor href="https://evolvemep.com/downloads/360-sync" target="_blank" rel="noreferrer">
                  <Text>Download the 360 Sync Desktop App</Text>
                </Anchor>
              </Card>
            )}
            <Card flex={1} bg="gray.0" radius="sm" p="lg">
              <Group align="start">
                <Stack gap="xl" mr={200}>
                  <Flex direction="column" gap="sm">
                    <Title order={4}>Helpful Links</Title>
                    <Anchor href="https://learning.evolvemep.com/account/signin" target="_blank" rel="noreferrer">
                      EVOLVE University Login
                    </Anchor>
                    <Anchor href="https://info.evolvemep.com/weekly-office-hours" target="_blank" rel="noreferrer">
                      Office Hours
                    </Anchor>
                    <Anchor href="https://evolvemep.com/downloads" target="_blank" rel="noreferrer">
                      Downloads
                    </Anchor>
                  </Flex>
                </Stack>
                <Flex direction="column" gap="sm">
                  <Title order={4}>Support</Title>
                  <Anchor href="https://evolvemep.com/contact-support" target="_blank" rel="noreferrer">
                    Contact Support
                  </Anchor>
                  {!user.isSsoUser && <Anchor onClick={() => setOpenModal(true)}>Reset Password</Anchor>}
                </Flex>
              </Group>
            </Card>
          </Group>

          <Group align="stretch">
            <Card flex={1} bg="gray.0" radius="sm" p="lg">
              <Stack>
                <Title order={4}>Your Licenses</Title>
                <LoadingOverlay visible={loadingLicenses || rolesLoading} />
                {(licenseData?.purchasedProducts ?? [])
                  .filter((productPool) => licenses.includes(productPool.productId))
                  .map((productPool) => (
                    <EvolveProductCard
                      key={productPool.productId}
                      radius="md"
                      icon={<LicenseIcon size="md" productId={productPool.productId} />}
                      title={productPool.product.productNameFormatted}
                      belowTitle={
                        isKeyOf(productPool.productId, roleLookup) ? (
                          <Text size="sm" c="dimmed">
                            Role: {roleLookup[productPool.productId]}
                          </Text>
                        ) : isKeyOf(productPool.productId, licensesThatNeedRoles) ? (
                          <Flex align="center" gap={6}>
                            <InfoIcon
                              color="red"
                              label={`To be assigned a ${licensesThatNeedRoles[productPool.productId]} role, contact your company administrator.`}
                            />
                            <Text c="red">
                              You do not have a {licensesThatNeedRoles[productPool.productId]} role. This may limit your
                              access to pages on this site.
                            </Text>
                          </Flex>
                        ) : undefined
                      }
                    />
                  ))}
              </Stack>
            </Card>
            <Card flex={1} bg="gray.0" radius="sm" p="lg">
              <Stack>
                <Title order={4}>Company Admins</Title>
                <LoadingOverlay visible={loadingAdmins} />
                {companyAdmins.map((adminUser) => (
                  <Card withBorder radius="md" key={adminUser.userId}>
                    <Title order={5}>
                      <UserInfo user={adminUser} size={getLicenseIconSize('md')} fz="h5" />
                    </Title>
                  </Card>
                ))}
              </Stack>
            </Card>
          </Group>
        </Box>
        <ResetPasswordModal
          opened={openModal}
          onClose={() => setOpenModal(false)}
          onComplete={() => setPasswordReset(true)}
        />
      </DesktopLayoutCard>
    </MobileUnsupportedGate>
  );
};
